import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { ArrowRightIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type CheckEmailData, type CheckEmailResponse, checkEmail } from 'sdk';
import { zCheckEmailBody } from 'sdk/zod.gen';
import { appConfig } from 'shared';
import type { z } from 'zod';
import type { ApiError } from '~/lib/api';
import { useAuthStore } from '~/modules/auth/auth-store';
import { SubmitButton } from '~/modules/common/form-fields/submit-button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '~/modules/ui/field';
import { Input } from '~/modules/ui/input';
import { defaultOnInvalid } from '~/utils/form-on-invalid';

const enabledStrategies: readonly string[] = appConfig.enabledAuthStrategies;
const emailEnabled = enabledStrategies.includes('passkey');

const formSchema = zCheckEmailBody;
type FormValues = z.infer<typeof formSchema>;

export function CheckEmailStep() {
  const { t } = useTranslation();

  const setStep = useAuthStore((state) => state.setStep);
  const setRestrictedMode = useAuthStore((state) => state.setRestrictedMode);

  const isMobile = window.innerWidth < 640;
  const title = appConfig.has.selfRegistration ? t('c:sign_in_or_up') : t('c:sign_in');

  const form = useForm<FormValues>({ resolver: zodResolver(formSchema), defaultValues: { email: '' } });

  // A browser that never signed in to the address gets the step that does not say whether it has an account.
  const { mutate: _checkEmail, isPending } = useMutation<CheckEmailResponse, ApiError, CheckEmailData['body']>({
    mutationFn: (body) => checkEmail({ body }),
    onSuccess: ({ recognized }) => {
      if (!recognized) setRestrictedMode(true);
      setStep('signIn', form.getValues('email'));
    },
    onError: (error: ApiError) => {
      if (error.status !== 429) return;
      setRestrictedMode(true);
      setStep('signIn', form.getValues('email'));
    },
  });

  const onSubmit = (body: FormValues) => _checkEmail(body);

  return (
    <Form {...form}>
      <h1 className="mt-4 pb-2 text-center text-2xl">{title}</h1>

      {emailEnabled && (
        <form onSubmit={form.handleSubmit(onSubmit, defaultOnInvalid)} className="flex flex-col gap-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              // Custom css due to html injection by browser extensions
              <FormItem className="gap-0">
                <FormLabel className="mb-2">{t('c:email')}</FormLabel>
                <FormControl>
                  <Input {...field} className="h-12" type="email" autoFocus={!isMobile} autoComplete="email" placeholder="name@example.com" />
                </FormControl>
                <FormMessage className="mt-2" />
              </FormItem>
            )}
          />
          <SubmitButton loading={isPending} className="w-full">
            {t('c:continue')}
            <ArrowRightIcon />
          </SubmitButton>
        </form>
      )}
    </Form>
  );
}
